import httpService from "./httpService";

/**
 * Contract details as returned by the /contracts/list batch API
 */
export interface ContractDetails {
  conId: number;
  symbol: string;
  secType: string;
  currency: string;
  exchange: string;
  primaryExchange: string;
  longName: string;
  ISIN?: string;
  Option_allowed?: boolean;
  Option_exchange?: string;
  Option_type?: string;
  minSize?: number; // Minimum trading lot size
}

/**
 * Response from the /contracts/list batch API
 */
export interface ContractListResponse {
  contracts: ContractDetails[];
}

/**
 * Request body for the /contracts/list batch API
 */
export interface ConIdListRequest {
  conIdList: number[];
}

/**
 * Tradable times info for a single contract
 */
export interface ContractTradableTimesInfo {
  timeZoneId: string | null;
  tradableTimesUtc: string | null;
}

/**
 * Response from the /contracts/tradable-times API
 */
export interface TradableTimesResponse {
  tradableTimes: Record<number, ContractTradableTimesInfo>;
}

/**
 * Service for contract-related API operations
 */
class ContractsService {
  /**
   * Get contract details for a list of contract IDs in a single batch request.
   * This is much more efficient than making individual requests per conId.
   * 
   * @param conIdList - Array of contract IDs to fetch details for
   * @returns Promise resolving to array of contract details
   */
  async getContractsByConIdList(conIdList: number[]): Promise<ContractDetails[]> {
    if (conIdList.length === 0) {
      return [];
    }

    const response = await httpService.post<ContractListResponse>(
      "/contracts/list",
      { conIdList },
      { requiresAuth: true }
    );

    return response.data.contracts;
  }

  /**
   * Build a lookup map from conId to symbol for quick access.
   * Useful for displaying ticker symbols in tables.
   * 
   * @param conIdList - Array of contract IDs to fetch
   * @returns Promise resolving to a Record mapping conId to symbol
   */
  async getSymbolMap(conIdList: number[]): Promise<Record<number, string>> {
    const contracts = await this.getContractsByConIdList(conIdList);
    
    const symbolMap: Record<number, string> = {};
    contracts.forEach((contract) => {
      symbolMap[contract.conId] = contract.symbol;
    });
    
    return symbolMap;
  }

  /**
   * Build a lookup map from conId to full contract details.
   * 
   * @param conIdList - Array of contract IDs to fetch
   * @returns Promise resolving to a Record mapping conId to ContractDetails
   */
  async getContractDetailsMap(conIdList: number[]): Promise<Record<number, ContractDetails>> {
    const contracts = await this.getContractsByConIdList(conIdList);
    
    const detailsMap: Record<number, ContractDetails> = {};
    contracts.forEach((contract) => {
      detailsMap[contract.conId] = contract;
    });
    
    return detailsMap;
  }

  /**
   * Get tradable times (trading hours and timezone) for a list of contract IDs.
   * 
   * @param conIdList - Array of contract IDs to get tradable times for
   * @returns Promise resolving to TradableTimesResponse
   */
  async getTradableTimes(conIdList: number[]): Promise<TradableTimesResponse> {
    if (conIdList.length === 0) {
      return { tradableTimes: {} };
    }

    const response = await httpService.post<TradableTimesResponse>(
      "/contracts/tradable-times",
      { conIdList },
      { requiresAuth: true }
    );

    return response.data;
  }

  /**
   * Get tradable times as a lookup map from conId to timeZoneId.
   * 
   * @param conIdList - Array of contract IDs to get tradable times for
   * @returns Promise resolving to a Record mapping conId to timeZoneId
   */
  async getTimeZoneMap(conIdList: number[]): Promise<Record<number, string>> {
    const response = await this.getTradableTimes(conIdList);

    const timeZoneMap: Record<number, string> = {};
    Object.entries(response.tradableTimes).forEach(([conIdStr, info]) => {
      const conId = parseInt(conIdStr, 10);
      if (info.timeZoneId) {
        timeZoneMap[conId] = info.timeZoneId;
      }
    });

    return timeZoneMap;
  }
}

export const contractsService = new ContractsService();
export default contractsService;

